/**
 * Referral CSV export (SCRUM-94). Exports the referral list with full
 * attribution + outcome for external reporting.
 *
 * Reuses the RFC-4180 `csvEscape` and blob-`downloadCsv` helpers from the
 * campaign export (SCRUM-210) rather than re-implementing them — the two
 * exports share the same low-level CSV mechanics, only the columns differ.
 */
import { csvEscape, downloadCsv } from '@/features/campaigns/wizard/csv-export';

import { effectiveOwner, REFERRAL_STATUS_META, type Referral } from './referrals-store';

/** The 9 export columns, in exact order (per the SCRUM-94 AC). */
export const REFERRAL_CSV_COLUMNS: readonly string[] = [
  'customer_name',
  'product',
  'match_count',
  'referred_by',
  'branch',
  'assigned_rm',
  'status',
  'flagged_date',
  'outcome',
];

/**
 * The calendar date a referral was flagged, derived from `submittedDaysAgo`
 * (the model carries whole-day granularity, not a timestamp). Local date —
 * this is a human-facing report column, not a UTC event time. `YYYY-MM-DD`.
 */
export function flaggedDate(submittedDaysAgo: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() - submittedDaysAgo);
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** The reportable outcome for a referral. */
export function referralOutcome(r: Referral): string {
  if (r.converted) return 'Converted';
  if (r.status === 'denied') return 'Denied';
  return 'Open';
}

/** Builds the full CSV text (header + one row per referral). */
export function buildReferralCsv(referrals: readonly Referral[], now: Date = new Date()): string {
  const rows: string[] = [REFERRAL_CSV_COLUMNS.map(csvEscape).join(',')];

  for (const r of referrals) {
    const row: (string | number | null)[] = [
      r.customerName,
      r.productName,
      // `match_count` is listed in the AC but is not carried on the referral
      // model (referrals are product-specific, not eligibility-scored). Emit
      // blank rather than fabricate a value.
      '',
      r.tellerName,
      r.branch,
      effectiveOwner(r).name,
      REFERRAL_STATUS_META[r.status].label,
      flaggedDate(r.submittedDaysAgo, now),
      referralOutcome(r),
    ];
    rows.push(row.map(csvEscape).join(','));
  }

  return rows.join('\n');
}

/** Builds the download filename: "referrals-{YYYY-MM-DD}.csv". */
export function referralCsvFilename(now: Date = new Date()): string {
  const stamp = `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  return `referrals-${stamp}.csv`;
}

/** Builds + triggers the browser download of the referral CSV. */
export function exportReferralsCsv(referrals: readonly Referral[], now: Date = new Date()): void {
  downloadCsv(buildReferralCsv(referrals, now), referralCsvFilename(now));
}
